/**
 * Automated quality checks (brief Section 8, QC-01..QC-20).
 *
 * Each check returns a result with outcome 'issue' (one or more
 * catalog messages), 'pass', or 'not-applicable' (with the reason —
 * usually a role the check needs was not assigned). Nothing is
 * fixed silently: issues carry a proposed action for the user to
 * approve (brief 5.2 step 4). Severities and proposed actions are
 * the brief's table; thresholds are configurable (Section 17).
 *
 * Documented readings (OPEN_QUESTIONS Q24): QC-17 aggregates per
 * indicator (a per-domain issue per area would flood the list; the
 * count and examples carry the information); QC-18 compares
 * statewide totals; QC-20 is not-applicable when no PSU variable is
 * assigned, because the sampled-PSU count of an area is a property
 * of the design variables.
 */
import { computeDirectEstimates } from '@sae/engine';
import { columnIndex, normalizeAreaId, tableToRespondents } from '@sae/io';
import { formatList, formatPct, renderMessage } from './catalog.js';
import { DEFAULT_COMBINED_LABEL } from './collapse.js';
import { distinctValuesOf, isMissingCell, medianOf, pearson, valueCountsOf, } from './profile.js';
import { recodeIndicators } from './rules.js';
export const DEFAULT_QC_THRESHOLDS = {
    extremeWeightRatio: 20,
    indicatorMissingShare: 0.3,
    sparseLevelRespondents: 30,
    covariateCorrelation: 0.9,
    populationTolerance: 0.1,
};
function cellsOf(table, name) {
    if (name === undefined)
        return [];
    const idx = columnIndex(table, name);
    if (idx < 0)
        return [];
    return table.rows.map((r) => r[idx] ?? null);
}
function result(checkId, issues, note) {
    return {
        checkId,
        outcome: issues.length > 0 ? 'issue' : 'pass',
        issues,
        ...(note !== undefined ? { note } : {}),
    };
}
function notApplicable(checkId, note) {
    return { checkId, outcome: 'not-applicable', issues: [], note };
}
export function runQcChecks(table, profile, context) {
    const t = { ...DEFAULT_QC_THRESHOLDS, ...context.thresholds };
    const term = context.respondentTerm ?? 'respondents';
    const roles = context.roles;
    const rules = context.indicatorRules ?? [];
    const ruleFor = (column) => rules.find((r) => r.sourceColumn === column);
    const msg = (code, values = {}) => renderMessage(code, values, term);
    const results = [];
    const weightCells = cellsOf(table, roles.weight);
    const areaCells = cellsOf(table, roles.areaId);
    const stratumCells = cellsOf(table, roles.stratum);
    const psuCells = cellsOf(table, roles.psu);
    const areaIds = new Set();
    for (const v of areaCells) {
        if (!isMissingCell(v))
            areaIds.add(normalizeAreaId(v));
    }
    // ---- QC-01: weight missing, zero, or negative (Block). --------
    if (roles.weight === undefined) {
        results.push(notApplicable('QC-01', 'no weight variable is assigned'));
    }
    else {
        let bad = 0;
        for (const v of weightCells) {
            if (typeof v !== 'number' || !(v > 0))
                bad++;
        }
        results.push(result('QC-01', bad > 0
            ? [
                {
                    checkId: 'QC-01',
                    severity: 'blocking',
                    message: msg('QC-01', { count: bad, column: roles.weight }),
                    scope: { kind: 'column', column: roles.weight },
                    proposedAction: 'exclude-rows',
                    values: { count: bad },
                },
            ]
            : []));
    }
    // ---- QC-02: extreme weights (Info). ---------------------------
    if (roles.weight === undefined) {
        results.push(notApplicable('QC-02', 'no weight variable is assigned'));
    }
    else {
        const positives = weightCells.filter((v) => typeof v === 'number' && v > 0);
        const median = medianOf(positives);
        const max = positives.length > 0 ? Math.max(...positives) : 0;
        const ratio = median > 0 ? max / median : 0;
        results.push(result('QC-02', ratio > t.extremeWeightRatio
            ? [
                {
                    checkId: 'QC-02',
                    severity: 'info',
                    message: msg('QC-02', {
                        ratio: ratio.toFixed(1),
                        column: roles.weight,
                    }),
                    scope: { kind: 'column', column: roles.weight },
                    proposedAction: 'none',
                    values: { ratio: Number(ratio.toFixed(3)) },
                },
            ]
            : []));
    }
    // ---- QC-03: PSU codes repeated across strata (Warn). ----------
    if (roles.psu === undefined || roles.stratum === undefined) {
        results.push(notApplicable('QC-03', 'both a PSU and a stratum variable are required'));
    }
    else {
        const strataByPsu = new Map();
        table.rows.forEach((row, i) => {
            const p = psuCells[i];
            const s = stratumCells[i];
            if (isMissingCell(p) || isMissingCell(s))
                return;
            let set = strataByPsu.get(p);
            if (!set) {
                set = new Set();
                strataByPsu.set(p, set);
            }
            set.add(s);
        });
        const repeated = [...strataByPsu.entries()]
            .filter(([, set]) => set.size > 1)
            .map(([p]) => p);
        results.push(result('QC-03', repeated.length > 0
            ? [
                {
                    checkId: 'QC-03',
                    severity: 'warning',
                    message: msg('QC-03', {
                        count: repeated.length,
                        psu: roles.psu,
                        stratum: roles.stratum,
                    }),
                    scope: { kind: 'column', column: roles.psu },
                    proposedAction: 'composite-key',
                    values: { count: repeated.length },
                },
            ]
            : []));
    }
    // ---- QC-04: strata with one PSU (Warn). -----------------------
    if (roles.stratum === undefined) {
        results.push(notApplicable('QC-04', 'no stratum variable is assigned'));
    }
    else {
        const psusByStratum = new Map();
        table.rows.forEach((row, i) => {
            const s = stratumCells[i];
            if (isMissingCell(s))
                return;
            const key = s;
            let set = psusByStratum.get(key);
            if (!set) {
                set = new Set();
                psusByStratum.set(key, set);
            }
            const p = psuCells[i];
            set.add(roles.psu !== undefined && !isMissingCell(p) ? `p:${String(p)}` : `row:${i}`);
        });
        const lonely = [...psusByStratum.entries()]
            .filter(([, set]) => set.size === 1)
            .map(([s]) => s);
        results.push(result('QC-04', lonely.length > 0
            ? [
                {
                    checkId: 'QC-04',
                    severity: 'warning',
                    message: msg('QC-04', {
                        count: lonely.length,
                        list: formatList(lonely),
                        method: context.lonelyPsuMethod ?? 'adjust',
                    }),
                    scope: { kind: 'file' },
                    proposedAction: 'lonely-psu-method',
                    values: { count: lonely.length },
                },
            ]
            : []));
    }
    // ---- QC-05: rows missing stratum or PSU (Block). --------------
    if (roles.stratum === undefined && roles.psu === undefined) {
        results.push(notApplicable('QC-05', 'no stratum or PSU variable is assigned'));
    }
    else {
        let missing = 0;
        table.rows.forEach((row, i) => {
            const sMissing = roles.stratum !== undefined && isMissingCell(stratumCells[i]);
            const pMissing = roles.psu !== undefined && isMissingCell(psuCells[i]);
            if (sMissing || pMissing)
                missing++;
        });
        results.push(result('QC-05', missing > 0
            ? [
                {
                    checkId: 'QC-05',
                    severity: 'blocking',
                    message: msg('QC-05', { count: missing }),
                    scope: { kind: 'file' },
                    proposedAction: 'exclude-rows-or-single-stage-fallback',
                    values: { count: missing },
                },
            ]
            : []));
    }
    // ---- QC-06: rows missing the area identifier (Warn). ----------
    if (roles.areaId === undefined) {
        results.push(notApplicable('QC-06', 'no area variable is assigned'));
    }
    else {
        const missing = areaCells.filter((v) => isMissingCell(v)).length;
        results.push(result('QC-06', missing > 0
            ? [
                {
                    checkId: 'QC-06',
                    severity: 'warning',
                    message: msg('QC-06', { count: missing, column: roles.areaId }),
                    scope: { kind: 'column', column: roles.areaId },
                    proposedAction: 'keep-in-design',
                    values: { count: missing },
                },
            ]
            : []));
    }
    // ---- QC-07: area identifiers unmatched across files (Warn). ---
    if (context.crossFileAreas === undefined || roles.areaId === undefined) {
        results.push(notApplicable('QC-07', 'needs a companion file with area codes to compare'));
    }
    else {
        const other = new Set(context.crossFileAreas.areas.map((a) => normalizeAreaId(a)));
        const onlyHere = [...areaIds].filter((a) => !other.has(a));
        const onlyThere = [...other].filter((a) => !areaIds.has(a));
        results.push(result('QC-07', onlyHere.length > 0 || onlyThere.length > 0
            ? [
                {
                    checkId: 'QC-07',
                    severity: 'warning',
                    message: msg('QC-07', {
                        other_label: context.crossFileAreas.label,
                        only_here: `${onlyHere.length} area codes (${formatList(onlyHere)})`,
                        only_there: `${onlyThere.length} area codes (${formatList(onlyThere)})`,
                    }),
                    scope: { kind: 'file' },
                    proposedAction: 'review-match-report',
                    values: {
                        unmatchedHere: onlyHere.length,
                        unmatchedThere: onlyThere.length,
                    },
                },
            ]
            : []));
    }
    // ---- Indicator checks QC-08/09/10 (per indicator column). -----
    const indicatorColumns = roles.indicators;
    const qc08 = [];
    const qc09 = [];
    const qc10 = [];
    for (const column of indicatorColumns) {
        const rule = ruleFor(column);
        const colCells = cellsOf(table, column);
        const distinct = distinctValuesOf(table, column);
        // Post-rule substantive values and missing share.
        let substantive = new Set();
        let missingShare = 0;
        if (rule !== undefined) {
            let missingCount = 0;
            for (const v of colCells) {
                if (isMissingCell(v)) {
                    missingCount++;
                    continue;
                }
                const raw = v;
                if (rule.missingValues.some((x) => x === raw))
                    missingCount++;
                else if (rule.yesValues.some((x) => x === raw) ||
                    rule.noValues.some((x) => x === raw)) {
                    substantive.add(raw);
                }
            }
            missingShare = colCells.length > 0 ? missingCount / colCells.length : 0;
        }
        else {
            const suspected = new Set((profile.columns.find((c) => c.name === column)?.suspectedMissing ?? []).map((v) => v.value));
            substantive = new Set(distinct.filter((v) => !suspected.has(v)));
            const p = profile.columns.find((c) => c.name === column);
            missingShare = p?.missingShare ?? 0;
        }
        // QC-08: not binary after rules.
        const problems = [];
        if (rule !== undefined && !rule.complete) {
            problems.push(`the rule does not classify ${rule.uncoveredValues.map(String).join(', ')}`);
        }
        if (rule === undefined && substantive.size > 2) {
            problems.push(`it has ${substantive.size} different values (${distinct.map(String).join(', ')}) and no rule`);
        }
        if (rule !== undefined && rule.yesValues.length === 0) {
            problems.push('the rule marks no value as yes');
        }
        if (rule !== undefined && rule.noValues.length === 0) {
            problems.push('the rule marks no value as no');
        }
        if (problems.length > 0) {
            qc08.push({
                checkId: 'QC-08',
                severity: 'blocking',
                message: msg('QC-08', { column, detail: problems.join('; ') }),
                scope: { kind: 'indicator', column, indicator: column },
                proposedAction: 'open-rule-builder',
                values: { substantiveValues: substantive.size },
            });
        }
        // QC-09: missing for more than the threshold share.
        if (missingShare > t.indicatorMissingShare) {
            qc09.push({
                checkId: 'QC-09',
                severity: 'warning',
                message: msg('QC-09', { column, pct: formatPct(missingShare) }),
                scope: { kind: 'indicator', column, indicator: column },
                proposedAction: 'report-only',
                values: { missingShare: Number(missingShare.toFixed(4)) },
            });
        }
        // QC-10: constant statewide (exactly one substantive value,
        // and at least one respondent answered).
        if (substantive.size === 1) {
            qc10.push({
                checkId: 'QC-10',
                severity: 'blocking',
                message: msg('QC-10', { column }),
                scope: { kind: 'indicator', column, indicator: column },
                proposedAction: 'exclude-indicator',
                values: {},
            });
        }
    }
    results.push(indicatorColumns.length === 0
        ? notApplicable('QC-08', 'no indicator variables are assigned')
        : result('QC-08', qc08));
    results.push(indicatorColumns.length === 0
        ? notApplicable('QC-09', 'no indicator variables are assigned')
        : result('QC-09', qc09));
    results.push(indicatorColumns.length === 0
        ? notApplicable('QC-10', 'no indicator variables are assigned')
        : result('QC-10', qc10));
    // ---- QC-11: sparse subgroup levels (Warn, per level). ---------
    const qc11 = [];
    for (const variable of roles.subgroups) {
        for (const vc of valueCountsOf(table, variable)) {
            if (vc.count < t.sparseLevelRespondents) {
                const label = vc.label ?? `Code ${String(vc.value)}`;
                qc11.push({
                    checkId: 'QC-11',
                    severity: 'warning',
                    message: msg('QC-11', {
                        variable,
                        level_label: label,
                        count: vc.count,
                        combined_label: DEFAULT_COMBINED_LABEL,
                    }),
                    scope: { kind: 'subgroup-level', variable, level: vc.value },
                    proposedAction: 'collapse-level',
                    values: { count: vc.count },
                });
            }
        }
    }
    results.push(roles.subgroups.length === 0
        ? notApplicable('QC-11', 'no subgroup variables are assigned')
        : result('QC-11', qc11));
    // ---- QC-12: unlabeled subgroup codes (Warn, per variable). ----
    const qc12 = [];
    for (const variable of roles.subgroups) {
        const col = table.columns[columnIndex(table, variable)];
        if (col === undefined || col.type !== 'number')
            continue;
        const counts = valueCountsOf(table, variable);
        const unlabeled = counts.filter((vc) => vc.label === undefined);
        if (unlabeled.length > 0) {
            qc12.push({
                checkId: 'QC-12',
                severity: 'warning',
                message: msg('QC-12', {
                    variable,
                    list: formatList(unlabeled.map((vc) => vc.value)),
                    examples: unlabeled
                        .slice(0, 3)
                        .map((vc) => `"Code ${String(vc.value)}"`)
                        .join(', '),
                }),
                scope: { kind: 'column', column: variable, variable },
                proposedAction: 'prompt-labels',
                values: { unlabeledLevels: unlabeled.length },
            });
        }
    }
    results.push(roles.subgroups.length === 0
        ? notApplicable('QC-12', 'no subgroup variables are assigned')
        : result('QC-12', qc12));
    const covariateViews = [];
    for (const name of roles.covariates) {
        const idx = columnIndex(table, name);
        if (idx < 0)
            continue;
        const byArea = new Map();
        const firstValue = new Map();
        table.rows.forEach((row, i) => {
            const a = areaCells[i];
            if (isMissingCell(a))
                return;
            const key = normalizeAreaId(a);
            const v = row[idx];
            if (typeof v !== 'number')
                return;
            let set = byArea.get(key);
            if (!set) {
                set = new Set();
                byArea.set(key, set);
            }
            set.add(v);
            if (!firstValue.has(key))
                firstValue.set(key, v);
        });
        const missingAreas = [...areaIds].filter((a) => !byArea.has(a));
        const varies = [...byArea.values()].some((set) => set.size > 1);
        covariateViews.push({
            name,
            missingAreas,
            variesWithinAreas: varies,
            perAreaValues: firstValue,
        });
    }
    if (context.covariateTable !== undefined) {
        const ct = context.covariateTable;
        const areaIdx = columnIndex(ct.table, ct.areaColumn);
        for (const name of ct.covariates) {
            const idx = columnIndex(ct.table, name);
            if (idx < 0 || areaIdx < 0)
                continue;
            const perArea = new Map();
            for (const row of ct.table.rows) {
                const a = row[areaIdx];
                const v = row[idx];
                if (isMissingCell(a) || typeof v !== 'number')
                    continue;
                perArea.set(normalizeAreaId(a), v);
            }
            covariateViews.push({
                name,
                missingAreas: [...areaIds].filter((a) => !perArea.has(a)),
                variesWithinAreas: false,
                perAreaValues: perArea,
            });
        }
    }
    const qc13 = [];
    const qc14 = [];
    for (const view of covariateViews) {
        if (view.missingAreas.length > 0) {
            qc13.push({
                checkId: 'QC-13',
                severity: 'warning',
                message: msg('QC-13', {
                    column: view.name,
                    count: view.missingAreas.length,
                    list: formatList(view.missingAreas),
                }),
                scope: { kind: 'covariate', column: view.name },
                proposedAction: 'exclude-covariate',
                values: { missingAreas: view.missingAreas.length },
            });
        }
        if (view.variesWithinAreas) {
            qc14.push({
                checkId: 'QC-14',
                severity: 'warning',
                message: msg('QC-14', { column: view.name }),
                scope: { kind: 'covariate', column: view.name },
                proposedAction: 'remove-candidate',
                values: {},
            });
        }
    }
    const hasCovariates = covariateViews.length > 0;
    results.push(hasCovariates ? result('QC-13', qc13) : notApplicable('QC-13', 'no covariate candidates are assigned'));
    results.push(hasCovariates ? result('QC-14', qc14) : notApplicable('QC-14', 'no covariate candidates are assigned'));
    // ---- QC-15: covariate pairs with |r| above threshold (Info). --
    const qc15 = [];
    const commonAreas = [...areaIds].sort();
    for (let i = 0; i < covariateViews.length; i++) {
        for (let j = i + 1; j < covariateViews.length; j++) {
            const a = covariateViews[i];
            const b = covariateViews[j];
            const xs = [];
            const ys = [];
            for (const area of commonAreas) {
                const x = a.perAreaValues.get(area);
                const y = b.perAreaValues.get(area);
                if (x !== undefined && y !== undefined) {
                    xs.push(x);
                    ys.push(y);
                }
            }
            if (xs.length < 3)
                continue;
            const r = pearson(xs, ys);
            if (Math.abs(r) > t.covariateCorrelation) {
                qc15.push({
                    checkId: 'QC-15',
                    severity: 'info',
                    message: msg('QC-15', {
                        column_a: a.name,
                        column_b: b.name,
                        r: r.toFixed(2),
                    }),
                    scope: { kind: 'pair', column: a.name, variable: b.name },
                    proposedAction: 'selection-resolves',
                    values: { correlation: Number(r.toFixed(4)) },
                });
            }
        }
    }
    results.push(covariateViews.length >= 2
        ? result('QC-15', qc15)
        : notApplicable('QC-15', 'fewer than two covariate candidates with area values'));
    // ---- QC-16: areas with zero respondents (Info). ---------------
    const referenceAreas = new Set();
    if (context.crossFileAreas !== undefined) {
        for (const a of context.crossFileAreas.areas) {
            referenceAreas.add(normalizeAreaId(a));
        }
    }
    if (context.populationCounts !== undefined) {
        for (const a of Object.keys(context.populationCounts)) {
            referenceAreas.add(normalizeAreaId(a));
        }
    }
    if (referenceAreas.size === 0) {
        results.push(notApplicable('QC-16', 'no companion area list or population counts to compare'));
    }
    else {
        const empty = [...referenceAreas].filter((a) => !areaIds.has(a)).sort();
        results.push(result('QC-16', empty.length > 0
            ? [
                {
                    checkId: 'QC-16',
                    severity: 'info',
                    message: msg('QC-16', {
                        count: empty.length,
                        list: formatList(empty),
                    }),
                    scope: { kind: 'file' },
                    proposedAction: 'out-of-sample-setting',
                    values: { count: empty.length },
                },
            ]
            : []));
    }
    // ---- QC-17: direct proportions of 0 or 1 (Info, per indicator).
    results.push(qc17(table, roles, rules, msg));
    // ---- QC-18: population vs weighted totals (Warn). -------------
    if (context.populationCounts === undefined || roles.weight === undefined) {
        results.push(notApplicable('QC-18', 'needs population counts and an assigned weight'));
    }
    else {
        let popTotal = 0;
        for (const v of Object.values(context.populationCounts))
            popTotal += v;
        let weightedTotal = 0;
        for (const v of weightCells) {
            if (typeof v === 'number')
                weightedTotal += v;
        }
        const rel = popTotal > 0 ? Math.abs(popTotal - weightedTotal) / popTotal : 0;
        results.push(result('QC-18', rel > t.populationTolerance
            ? [
                {
                    checkId: 'QC-18',
                    severity: 'warning',
                    message: msg('QC-18', {
                        pop_total: Math.round(popTotal).toLocaleString('en-US'),
                        weighted_total: Math.round(weightedTotal).toLocaleString('en-US'),
                        pct: formatPct(rel),
                    }),
                    scope: { kind: 'file' },
                    proposedAction: 'check-population-file',
                    values: { relativeDifference: Number(rel.toFixed(4)) },
                },
            ]
            : []));
    }
    // ---- QC-19: duplicate respondent identifiers (Block). ---------
    if (roles.respondentId === undefined) {
        results.push(notApplicable('QC-19', 'no respondent identifier is assigned'));
    }
    else {
        const counts = valueCountsOf(table, roles.respondentId);
        const dupes = counts.filter((vc) => vc.count > 1);
        const excess = dupes.reduce((a, vc) => a + (vc.count - 1), 0);
        results.push(result('QC-19', dupes.length > 0
            ? [
                {
                    checkId: 'QC-19',
                    severity: 'blocking',
                    message: msg('QC-19', {
                        column: roles.respondentId,
                        count: dupes.length,
                        list: formatList(dupes.map((vc) => vc.value)),
                    }),
                    scope: { kind: 'column', column: roles.respondentId },
                    proposedAction: 'resolve-duplicates',
                    values: { duplicatedValues: dupes.length, excessRows: excess },
                },
            ]
            : []));
    }
    // ---- QC-20: areas below the disclosure PSU minimum (Warn). ----
    const minPsus = context.disclosure?.minPsusPerArea ?? 1;
    if (roles.psu === undefined || roles.areaId === undefined) {
        results.push(notApplicable('QC-20', 'needs a PSU variable to count sampled PSUs per area'));
    }
    else {
        const psusByArea = new Map();
        table.rows.forEach((row, i) => {
            const a = areaCells[i];
            if (isMissingCell(a))
                return;
            const key = normalizeAreaId(a);
            let set = psusByArea.get(key);
            if (!set) {
                set = new Set();
                psusByArea.set(key, set);
            }
            const p = psuCells[i];
            if (!isMissingCell(p)) {
                const s = stratumCells[i];
                set.add(`${isMissingCell(s) ? '' : String(s)}|${String(p)}`);
            }
        });
        const below = [...psusByArea.entries()]
            .filter(([, set]) => set.size < minPsus)
            .map(([a]) => a)
            .sort();
        results.push(result('QC-20', below.length > 0
            ? [
                {
                    checkId: 'QC-20',
                    severity: 'warning',
                    message: msg('QC-20', {
                        count: below.length,
                        min_psus: minPsus,
                        list: formatList(below),
                    }),
                    scope: { kind: 'file' },
                    proposedAction: 'apply-disclosure-rules',
                    values: { count: below.length },
                },
            ]
            : []));
    }
    return results;
}
/** QC-17 via the engine's direct estimates on rule-recoded data. */
function qc17(table, roles, rules, msg) {
    if (roles.areaId === undefined ||
        roles.weight === undefined ||
        roles.indicators.length === 0 ||
        rules.length === 0) {
        return notApplicable('QC-17', 'needs area, weight, indicators, and indicator rules to compute direct proportions');
    }
    try {
        const recoded = recodeIndicators(table, rules);
        const conversion = tableToRespondents(recoded.table, {
            area: roles.areaId,
            weight: roles.weight,
            ...(roles.stratum !== undefined ? { stratum: roles.stratum } : {}),
            ...(roles.psu !== undefined ? { psu: roles.psu } : {}),
            indicators: rules.map((r) => r.sourceColumn),
            subgroups: roles.subgroups,
        });
        if (conversion.respondents.length === 0) {
            return notApplicable('QC-17', 'the microdata did not convert to respondents');
        }
        const subgroupSpecs = roles.subgroups.map((variable) => ({
            variable,
            levels: distinctValuesOf(table, variable).map((value) => ({ value })),
        }));
        const direct = computeDirectEstimates(conversion.respondents, {
            indicators: rules.map((r) => r.id),
            subgroups: subgroupSpecs,
            lonelyPsu: 'adjust',
            confidence: 0.95,
        });
        const byIndicator = new Map();
        for (const d of direct) {
            if (d.domain.areaId === '__statewide__')
                continue;
            if (d.n > 0 && (d.estimate === 0 || d.estimate === 1)) {
                let arr = byIndicator.get(d.domain.indicatorId);
                if (!arr) {
                    arr = [];
                    byIndicator.set(d.domain.indicatorId, arr);
                }
                arr.push({
                    area: d.domain.areaId,
                    cell: d.domain.subgroupVariable !== undefined
                        ? `${d.domain.areaId} (${String(d.domain.subgroupLevel)})`
                        : d.domain.areaId,
                });
            }
        }
        const issues = [];
        for (const [indicator, domains] of byIndicator) {
            issues.push({
                checkId: 'QC-17',
                severity: 'info',
                message: msg('QC-17', {
                    indicator,
                    count: domains.length,
                    list: formatList(domains.map((d) => d.cell)),
                }),
                scope: { kind: 'indicator', indicator },
                proposedAction: 'none',
                values: { count: domains.length },
            });
        }
        return result('QC-17', issues);
    }
    catch {
        return notApplicable('QC-17', 'direct estimates could not be computed for this design');
    }
}
/** Convenience: only the issues, across all twenty checks. */
export function qcIssues(results) {
    return results.flatMap((r) => r.issues);
}
//# sourceMappingURL=qc.js.map